import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_LIFTED, DARK_TRACK } from '../../styles/elevation';

/**
 * SegmentedControl variants — mirror the Figma "Flashka Design System"
 * `SegmentedControl` (file `YZK9r3wDhvw1pY60KlVEIj`). Reference nodes:
 *   - 1528:1833 — assembled control (container + all 7 sizes), light.
 *   - 3172:14143 — assembled control, DARK.
 *   - 1494:1338 — the Segment component set (all types/states/keybind), light.
 *   - 3172:13235 — the Segment component set, DARK.
 *   - 4036:23308 / 24117 / 13218 — Text+icon+Keybind at M / XXS / XXL.
 *
 * DARK MODE — the palette auto-inverts every shade in `.dark` (web) /
 * `.dark:root` (native) via `generate-css-vars.cjs`, and that alone covers the
 * track, washes, and the primary/secondary pill fills+borders. BUT the dark
 * Figma nodes are NOT a pure token-for-token inversion — several elements use
 * *shifted* tokens (verified via get_variable_defs on the dark nodes, resolved
 * dark hex in brackets):
 *   - (superseded 2026-10-15) the tertiary pill + track used to shift to
 *     `secondary-200`/`-300` in dark (3172:13236); they now take the light
 *     tokens in both modes, matching the All Sources tab bar.
 *   - active label/icon: primary `primary-900` (#ede9fe), secondary/tertiary
 *     `primary-800` (#ddd6ff) — one ramp-step lighter than the inverted light
 *     tokens (3172:13242 / 13239 / 13236).
 *   - inactive hover/pressed label: brightens to `secondary-900` (#f5f5f5) —
 *     light mode keeps `secondary-500` (3172:13362/13365 vs 1588:1715/1719).
 *   - inactive focus ring: `secondary-950` (#ffffff) — light uses
 *     `secondary-600` (3172:13434 vs 1528:1645).
 * These are expressed as explicit `dark:` overrides below (both apps use
 * class-based dark, and the tokens resolve their dark values automatically).
 *
 * ANIMATION — the active pill's fill/border/shadow live on a SEPARATE moving
 * layer (`activeSegmentVariants`), not on the segment itself, so it can slide
 * between segments. Both platforms use one measured overlay positioned to the
 * active segment's box (CSS transform+width transition on web; RN `Animated`
 * on native) — no framer-motion, keeping shared's dependency surface minimal.
 * Each segment only paints its text/icon colour + the inactive hover/pressed
 * wash.
 *
 * Cross-platform: `web:`-gated transitions/focus/scale/box-shadows stay off
 * native (NativeWind v4 can't render them reliably on RN 0.83). The pill drop
 * shadow is re-applied as an RN `shadowColor`/`elevation` style in the native
 * file, and the container's INSET shadow is re-applied there too via an inline
 * `boxShadow` (RN 0.83 renders inset box-shadow natively — see the native
 * file's `NATIVE_INSET_SHADOW`), so the recessed track reads on both platforms.
 * The track's `web:backdrop-blur-[6px]` is mirrored on iOS by a real expo-blur
 * `BlurView` (`TrackBlur` in the native file, same treatment as Button's
 * GlassBackground); Android degrades to the translucent fill alone.
 */

/**
 * The outer track that holds the segments (Figma 1528:1833 / 3172:14143): a
 * translucent `secondary-50/70` fill + `secondary-200` border + an inset
 * shadow (`inset 0 2 6 #0000001A`). Emitted as `web:shadow-[inset…]` on web
 * and re-applied as an inline `boxShadow` on native (see `NATIVE_INSET_SHADOW`
 * in the native file). Segments sit 2px apart (`gap-0.5`, all sizes/styles —
 * deliberate product choice 2026-07-14; the Figma nodes have them flush).
 * NATIVE: the gap lives on the padding-free inner row in the native file (the
 * segments aren't direct children of this track there) — keep the two in sync.
 */
export const segmentedControlVariants = cva(
  // `relative` anchors the moving active pill (an absolute overlay) — on web
  // it's also the segments' offsetParent, so `offsetLeft`/`offsetTop`
  // measurements are track-relative. The absolute pill is out of flow, so
  // `gap-0.5` only spaces the segments.
  // ONE track treatment in both modes — the auto-inverting `secondary-200`
  // hairline, the same shell as the deck's All Sources tab bar
  // (`MaterialsTabBar`). Dark used to shift it to `secondary-300` (Figma
  // 4455:14415), which read as a heavy outline next to that bar; product
  // overruled the node 2026-10-15.
  'relative inline-flex flex-row items-center gap-0.5 border ' +
    'bg-secondary-50/70 border-secondary-200 ' +
    // Dark: the track is recessed one step below its host (`DARK_TRACK`) and
    // the active pill is lifted above it (elevation.ts).
    `${DARK_TRACK} ` +
    'web:shadow-[inset_0px_2px_6px_0px_rgba(0,0,0,0.1)] web:backdrop-blur-[6px]',
  {
    variants: {
      // Track padding + radius per size, verified against the assembled
      // control node 1528:1833 (2026-07-14 MCP read). The radius depends on
      // the CONTENT TYPE: text/hybrid controls use 12.5/12/14/16/18/20/20
      // (deliberately non-concentric with the pill), while ICON-ONLY controls
      // step up to the concentric ramp — see the `iconOnly` compounds below.
      // xxs uses a 0.8px border (spec) — `border-[0.8px]` overrides the base
      // `border` via twMerge.
      size: {
        xxs: 'border-[0.8px] p-[2.5px] rounded-[12.5px]',
        xs: 'p-1 rounded-[12px]',
        s: 'p-1 rounded-[14px]',
        m: 'p-1 rounded-[16px]',
        l: 'p-1 rounded-[18px]',
        xl: 'p-1 rounded-[20px]',
        xxl: 'p-1 rounded-[20px]',
      },
      /** Every segment is icon-only → the track radius steps up to stay
       *  concentric with the square cells (Figma icon-only control variants:
       *  xs/s → 16, m → 18, l → 20; xl/xxl already 20). */
      iconOnly: { true: '', false: '' },
      fullWidth: {
        true: 'flex w-full',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: ['xs', 's'], class: 'rounded-[16px]' },
      { iconOnly: true, size: 'm', class: 'rounded-[18px]' },
      { iconOnly: true, size: 'l', class: 'rounded-[20px]' },
    ],
    defaultVariants: { size: 'l', iconOnly: false, fullWidth: false },
  },
);

/**
 * Per-size item geometry: height · horizontal padding · corner radius. Shared
 * verbatim by the segment cell and the moving active pill so the pill's box
 * lines up exactly with the segment it sits under. The ACTIVE radius is
 * uniform per size across all segment types (1494:1338 set; the earlier
 * 2px-smaller "compact" radius was a misread and has been removed — the set's
 * INACTIVE l/xl nodes still carry a stale 14px radius from before the 4036
 * refresh; we keep the uniform active value). Icon-only becomes a square cell
 * (width == height).
 *
 * Horizontal padding (verified 2026-07-14 on 1494:1337/1335, 1528:1701,
 * 4036:23075 + the assembled 1528:1833):
 *   - LABEL-ONLY segments: symmetric px 10/8/10/12/12/12/12 (the base here).
 *   - ICON+LABEL and KEYBIND segments: ASYMMETRIC — pl 6/8/10/12/12/12/12
 *     with pr = pl + 4 (see the `iconText` compounds below). Keybind
 *     segments use the SAME padding as icon+label (4036:23075 == 1494:1337).
 *   - ICON-ONLY: square, px-0 (compounds below).
 * XXL radius is 16 (was 18) per the 4036:* refresh.
 */
const sizeGeometry = {
  xxs: 'h-6 px-2.5 rounded-[10px]',
  xs: 'h-7 px-2 rounded-[11px]',
  s: 'h-8 px-2.5 rounded-[12px]',
  m: 'h-9 px-3 rounded-[14px]',
  l: 'h-10 px-3 rounded-[16px]',
  xl: 'h-11 px-3 rounded-[16px]',
  xxl: 'h-12 px-3 rounded-[16px]',
} as const;

/** A single segment cell (the pressable). Owns geometry + text/icon colour. */
export const segmentVariants = cva(
  // Inactive focus = a flush 1px `secondary-600` outline (Figma inactive Focus
  // node 1635:14980); a `ring-offset-0` ring renders the same 1px line while
  // reliably overriding the Pressable primitive's default ring — same proven
  // pattern as Button/Chip. Active segments override the ring below. The dark
  // node (3172:13434) shifts the ring to `secondary-950` (#ffffff in dark),
  // not the auto-inverted secondary-600.
  'group relative inline-flex flex-row items-center justify-center border-0 bg-transparent ' +
    'web:transition-[transform,background-color,color] web:duration-150 web:ease-out ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0 web:focus-visible:ring-secondary-600 ' +
    'web:dark:focus-visible:ring-secondary-950',
  {
    variants: {
      size: sizeGeometry,
      active: {
        // Active text/icon colour lives in the activeStyle compounds below.
        true: '',
        // Inactive segment: transparent at rest with `secondary-500` text —
        // in BOTH modes, all states (the refreshed dark set 4455:13749/13194
        // keeps the label at secondary-500 on hover/pressed; the old dark
        // brighten-to-900 is obsolete). Hover / pressed add a subtle
        // inverting wash — deliberately one palette step past the spec's
        // near-invisible shades (spec hover secondary-50 / pressed
        // secondary-100; the near-white shade read as no-hover, PR #38
        // review). Web icons follow via `currentColor`. NativeWind drops
        // `enabled:*` chains entirely on native, so the pressed wash is
        // mirrored with `native:active:*` (same pattern as Button).
        false:
          'text-fg-subtle ' +
          'enabled:hover:bg-secondary-100 ' +
          'enabled:active:bg-secondary-200 ' +
          'native:active:bg-secondary-200 ' +
          'web:enabled:active:scale-[0.98]',
      },
      activeStyle: { primary: '', secondary: '', tertiary: '' },
      iconOnly: { true: '', false: '' },
      /** Icon+label — or a keybind keycap — switches to asymmetric padding. */
      iconText: { true: '', false: '' },
      keybind: { true: '', false: '' },
      fullWidth: { true: 'flex-1', false: '' },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [
      // ── Active text/icon colour (fill/border/shadow live on the pill) ──────
      // The refreshed DARK Segment set (4455:12257, read 2026-07-14) uses the
      // PLAIN auto-inverting tokens: secondary label = primary-700 (#c4b4ff in
      // dark), primary label = primary-50 light / primary-950
      // dark — both resolve #f5f3ff, i.e. constant across modes. Tertiary is a
      // deliberate Flashka departure from that set — neutral, see below. The old
      // "one ramp-step lighter" dark shifts (3172:* nodes) are obsolete.
      // Keep `SegmentedControl.icons.ts` in sync (native icons can't read
      // currentColor).
      {
        active: true,
        activeStyle: 'primary',
        class: 'text-primary-50 dark:text-primary-950',
      },
      {
        active: true,
        activeStyle: 'secondary',
        class: 'text-primary-700',
      },
      // Tertiary is the low-emphasis, iOS-style style — its active label reads
      // NEUTRAL (`secondary-800`, auto-inverting to #e5e5e5 in dark), not the
      // brand purple the primary/secondary styles use. The purple pill fill
      // already carries the emphasis; a purple label on the white tertiary
      // pill made every default control shout.
      {
        active: true,
        activeStyle: 'tertiary',
        class: 'text-secondary-800',
      },
      // ── Icon-only → square cell (width tracks height, no horizontal pad) ──
      { iconOnly: true, size: 'xxs', class: 'w-6 px-0' },
      { iconOnly: true, size: 'xs', class: 'w-7 px-0' },
      { iconOnly: true, size: 's', class: 'w-8 px-0' },
      { iconOnly: true, size: 'm', class: 'w-9 px-0' },
      { iconOnly: true, size: 'l', class: 'w-10 px-0' },
      { iconOnly: true, size: 'xl', class: 'w-11 px-0' },
      { iconOnly: true, size: 'xxl', class: 'w-12 px-0' },
      // ── Icon+label / keybind → asymmetric padding, pr = pl + 4 (Figma
      // 1494:1337 pl-12 pr-16 at l; ramp from the assembled 1528:1833) ──────
      { iconText: true, size: 'xxs', class: 'pl-1.5 pr-2.5' },
      { iconText: true, size: 'xs', class: 'pl-2 pr-3' },
      { iconText: true, size: 's', class: 'pl-2.5 pr-3.5' },
      { iconText: true, size: ['m', 'l', 'xl', 'xxl'], class: 'pl-3 pr-4' },
      // Keybind at xxs uses its own insets (4036 keybind set: pl 10 / pr 12).
      { keybind: true, size: 'xxs', class: 'pl-2.5 pr-3' },
      // ── Active focus ring (Figma 1528:1638 / 1635:15434 / 1635:15447 ·
      // dark 3172:13308/13312/13316): a 1px `primary-900` ring lifted 1px off
      // the pill by a ring-offset band painted in the pill's own fill colour —
      // Figma's focus anatomy is an outer wrapper with `border + p-px` whose
      // bg equals the pill fill. Box-shadow spread grows the corner radius
      // automatically, so the ring stays concentric with the pill. The offset
      // overrides the base's flush `ring-offset-0`; INACTIVE focus stays flush
      // (its Figma node is a plain border on the segment box). An earlier
      // build drew the active ring flush "by design" — it read as a heavy
      // double border glued to the pill and was rejected in review.
      // The `web:dark:` colour repeats the same token on purpose: the base's
      // inactive `web:dark:focus-visible:ring-secondary-950` carries higher
      // CSS specificity (`.dark …`), so actives need a dark-scoped override
      // for twMerge to strip it (`primary-900` resolves #ede9fe in dark).
      {
        active: true,
        class:
          'web:focus-visible:ring-primary-900 web:dark:focus-visible:ring-primary-900 ' +
          'web:focus-visible:ring-offset-1',
      },
      {
        active: true,
        activeStyle: 'primary',
        class: 'web:focus-visible:ring-offset-primary-500',
      },
      {
        active: true,
        activeStyle: 'secondary',
        class: 'web:focus-visible:ring-offset-primary-50',
      },
      {
        active: true,
        activeStyle: 'tertiary',
        class: 'web:focus-visible:ring-offset-secondary-50',
      },
      // ── Disabled — `opacity-50`, active AND inactive, both modes (light
      // 1588:1723/1729 · dark 3172:13359/13245) ─────────────────────────────
      { disabled: true, class: '!opacity-50' },
    ],
    defaultVariants: {
      size: 'l',
      active: false,
      activeStyle: 'tertiary',
      iconOnly: false,
      keybind: false,
      fullWidth: false,
      disabled: false,
    },
  },
);

/**
 * The moving active pill — fill + 1px border + drop shadow, positioned to
 * overlay the active segment. Shares the segment's per-size geometry so its
 * box aligns exactly. Primary/secondary fills+borders are
 * semantic tokens whose auto-inversion lands exactly on the dark nodes
 * (resolved dark hex in brackets):
 *   - primary   — `primary-500` fill, `primary-600` border (dark: same tokens
 *                 → #8e51ff / #a684ff).
 *   - secondary — `primary-50` fill, `primary-100` border (dark: same tokens
 *                 → #2f0d68 / #4d179a).
 *   - tertiary  — `secondary-50` fill + `secondary-200` border in BOTH modes
 *                 (white / #e5e5e5 light; the surface + hairline in dark) —
 *                 the All Sources tab bar's active cell.
 */
export const activeSegmentVariants = cva(
  // No `inset-0`: web fills the parent segment via `inset-0` added at the call
  // site; native positions the overlay explicitly (left/top/width/height).
  'pointer-events-none absolute border',
  {
    variants: {
      size: {
        xxs: 'border-[0.8px] rounded-[10px]',
        xs: 'rounded-[11px]',
        s: 'rounded-[12px]',
        m: 'rounded-[14px]',
        l: 'rounded-[16px]',
        xl: 'rounded-[16px]',
        xxl: 'rounded-[16px]',
      },
      activeStyle: {
        primary: 'bg-primary-500 border-primary-600',
        secondary: 'bg-primary-50 border-primary-100',
        // Same tokens in both modes = the All Sources tab bar's active cell
        // (`MaterialsTabBar` CELL_ACTIVE). The dark `secondary-200` fill +
        // `secondary-300` border (Figma 3172:13236) read as a heavy raised
        // slab beside that bar; product overruled the node 2026-10-15.
        tertiary: `bg-secondary-50 border-secondary-200 ${DARK_LIFTED}`,
      },
      /** ICON-ONLY pills carry NO drop shadow (Figma 1577:884 + the assembled
       *  1528:1833 icon-only variants render with zero shadow bleed, while
       *  text variants bleed the full Shadow-Secondary bounds). The native
       *  mirror gates `NATIVE_TAB_SHADOW` on the same flag. */
      iconOnly: { true: '', false: 'web:shadow-tab' },
    },
    defaultVariants: { size: 'l', activeStyle: 'tertiary', iconOnly: false },
  },
);

/**
 * Per-size label typography. xxs–s = B3 (Mona Sans Medium 12), m/l = Body-2
 * (**Inter SemiBold 14** — verified on 1494:1337/1335; `font-inter-semibold`
 * resolves the Inter_600SemiBold face on native and falls back to `Inter` +
 * weight 600 on web), xl = B1 (Mona 16), xxl = B4 (Mona 18).
 */
export const segmentTextVariants = cva(
  'whitespace-nowrap font-medium !leading-[normal]',
  {
    variants: {
      size: {
        xxs: 'text-xs native:font-mona-medium',
        xs: 'text-xs native:font-mona-medium',
        s: 'text-xs native:font-mona-medium',
        m: 'text-sm font-semibold font-inter-semibold',
        l: 'text-sm font-semibold font-inter-semibold',
        xl: 'text-base native:font-mona-medium',
        xxl: 'text-lg native:font-mona-medium',
      },
    },
    defaultVariants: { size: 'l' },
  },
);

/**
 * Segment label COLOUR per (active, activeStyle) — the same tokens as the
 * cell-level compounds in `segmentVariants` (which web icons/labels inherit
 * via CSS `currentColor` cascade). Applied DIRECTLY to the label `<Text>` in
 * `SegmentContent` because RN `Text` does not inherit colour from its parent
 * View — without this, native labels rendered in the RN default (black)
 * instead of the DS tones. Harmless duplication on web (same colour wins).
 * Keep in sync with `segmentVariants`' active compounds and
 * `SegmentedControl.icons.ts` (native icon hexes).
 */
export const segmentLabelColorVariants = cva('', {
  variants: {
    active: { true: '', false: 'text-fg-subtle' },
    activeStyle: { primary: '', secondary: '', tertiary: '' },
  },
  compoundVariants: [
    {
      active: true,
      activeStyle: 'primary',
      class: 'text-primary-50 dark:text-primary-950',
    },
    {
      active: true,
      activeStyle: 'secondary',
      class: 'text-primary-700',
    },
    // Neutral active label — see the matching compound in `segmentVariants`.
    {
      active: true,
      activeStyle: 'tertiary',
      class: 'text-secondary-800',
    },
  ],
  defaultVariants: { active: false, activeStyle: 'tertiary' },
});

/*
 * Keybind keycap CVAs moved to `../KeybindChip/KeybindChip.tsx` (shared with
 * Button and Tooltip/Popover) — SegmentContent renders `<KeybindChip>` with
 * tone mapped to the chip's `primary` / `secondary` colorways.
 */

/** Chip tier per control size — Figma reuses "Small" for xxs–m, "Default" for l–xxl. */
export const KEYBIND_CHIP_SIZE = {
  xxs: 'sm',
  xs: 'sm',
  s: 'sm',
  m: 'sm',
  l: 'md',
  xl: 'md',
  xxl: 'md',
} as const;

export type SegmentedControlVariantProps = VariantProps<typeof segmentVariants>;
